/** Default field prefix. A second address on the same entity uses its own. */
export const ADDRESS_PREFIX = 'address';
/**
 * Prefix of the **delivery** address block (spec 0047 §4.3 / **D36**).
 *
 * The first entity to carry a second prefix is `Shipment`, and it carries *only*
 * this one — a shipment has no address of its own, it has a destination. The
 * constant exists so the type, the descriptor, the form and the grid column all
 * spell it the same way; a literal `'deliveryAddress'` typed twice is how nine
 * columns become eighteen, and `pgSchema.service.ts` never drops a column.
 */
export const DELIVERY_ADDRESS_PREFIX = 'deliveryAddress';
/**
 * The nine keys of the group, in the order the user fills them (spec §5):
 * CEP → Logradouro → Número → Complemento → Bairro → **Estado → Cidade**.
 *
 * State before city is not cosmetic (D23): the municipality list is fetched
 * *from* the chosen UF (D27), so without a UF the city field has nothing to
 * offer.
 *
 * All nine exist on every entity that has an address (D22) — the mode governs
 * whether the ids get filled, never whether the fields exist.
 */
export const ADDRESS_FIELD_KEYS = [
    'zipCode',
    'street',
    'number',
    'complement',
    'district',
    'state',
    'stateId',
    'city',
    'cityId',
];
/**
 * `addressFieldName('address', 'zipCode') === 'addressZipCode'`.
 *
 * An empty prefix yields the bare key — the escape hatch for an entity whose
 * columns are already unprefixed. Not used by the standard block.
 */
export function addressFieldName(prefix, key) {
    if (!prefix)
        return key;
    return `${prefix}${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}
/** Reads the group out of a flat entity: `pickAddress(party, 'address')`. */
export function pickAddress(entity, prefix = ADDRESS_PREFIX) {
    const record = entity;
    const address = {};
    for (const key of ADDRESS_FIELD_KEYS) {
        const raw = record[addressFieldName(prefix, key)];
        if (raw == null)
            continue;
        address[key] = String(raw);
    }
    return address;
}
/**
 * True when every field of the group is absent or blank.
 *
 * This is the predicate behind D16: an empty address is **valid** (it is
 * optional on almost every record); a half-filled one is not.
 */
export function isAddressEmpty(address) {
    return ADDRESS_FIELD_KEYS.every((key) => (address[key] ?? '').trim() === '');
}
//# sourceMappingURL=postalAddress.js.map